'use client';

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@ui/button';
import { usePathname } from 'next/navigation';
import { SmolLink } from '@web/atoms';
import { useReporter } from '../../reporter';
import {
  userViewsCookieBanner,
  userClicksCookieBannerCTA,
  userClicksCookieBannerLink,
} from './events';
import {
  StyledCookieBanner,
  Heading,
  Subheading,
  BottomRowContainer,
  LinksContainer,
} from './styles';

export const CookieBanner = ({
  heading,
  subheading,
  buttonCopy = 'Accept',
  buttonVariant = 'primary',
  preLinkCopy = 'Read our',
  links,
  excludedPaths,
}) => {
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const pathname = usePathname();
  const reporter = useReporter();

  useEffect(() => {
    const hasAccepted = sessionStorage.getItem('cookie_banner_accepted');
    const isExcludedPath = excludedPaths?.some(({ subpath }) =>
      pathname.startsWith(subpath),
    );

    if (!hasAccepted && !isExcludedPath) {
      setShowCookieBanner(true);
      reporter.tag(userViewsCookieBanner(pathname));
    } else {
      setShowCookieBanner(false);
    }
  }, [pathname, excludedPaths, reporter]);

  const handleAccept = () => {
    reporter.tag(userClicksCookieBannerCTA(buttonCopy, pathname));
    sessionStorage.setItem('cookie_banner_accepted', 'true');
    setShowCookieBanner(false);
  };

  const handleLinkClick = linkText => {
    reporter.tag(userClicksCookieBannerLink(linkText, pathname));
  };

  return (
    <StyledCookieBanner $isOpen={showCookieBanner}>
      <Heading>{heading}</Heading>
      <Subheading>{subheading}</Subheading>
      <BottomRowContainer>
        <Button
          variant={buttonVariant}
          onClick={handleAccept}
          verticalPadding={3}
          horizontalPadding={9}
        >
          {buttonCopy}
        </Button>
        <LinksContainer>
          {links.length >= 1 && (
            <>
              {preLinkCopy}{' '}
              <SmolLink
                underline
                href={links[0].target}
                onClick={() => handleLinkClick(links[0].text)}
              >
                {links[0].text}
              </SmolLink>
            </>
          )}
          {links.length === 2 && (
            <>
              {' and '}
              <SmolLink
                underline
                href={links[1].target}
                onClick={() => handleLinkClick(links[1].text)}
              >
                {links[1].text}
              </SmolLink>
            </>
          )}
        </LinksContainer>
      </BottomRowContainer>
    </StyledCookieBanner>
  );
};

CookieBanner.displayName = 'CookieBanner';

CookieBanner.propTypes = {
  heading: PropTypes.string,
  subheading: PropTypes.string,
  buttonCopy: PropTypes.string,
  buttonVariant: PropTypes.string,
  preLinkCopy: PropTypes.string,
  links: PropTypes.array,
  excludedPaths: PropTypes.array,
};
